import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Mail, Shield, X, Check, Ban, UserPlus, KeyRound, Copy, Eye, EyeOff } from 'lucide-react';

const ROLE_LABELS = { 
  admin: 'Administrador', 
  consul: 'Cônsul',
  editor: 'Editor', 
  publisher: 'Publicador', 
  consular_agent: 'Agente Consular',
  financial: 'Financeiro',
  auditor: 'Auditor',
  moderator: 'Moderador', 
  user: 'Utilizador' 
};
const ROLE_COLORS = { 
  admin: 'bg-red-100 text-red-700', 
  consul: 'bg-purple-100 text-purple-800',
  editor: 'bg-blue-100 text-blue-700', 
  publisher: 'bg-green-100 text-green-700', 
  consular_agent: 'bg-indigo-100 text-indigo-700',
  financial: 'bg-amber-100 text-amber-700',
  auditor: 'bg-teal-100 text-teal-700',
  moderator: 'bg-yellow-100 text-yellow-700', 
  user: 'bg-gray-100 text-gray-600' 
};

export default function Users() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ email: '', username: '', full_name: '', password: '', role: 'publisher' });
  const [saving, setSaving] = useState(false);
  
  // Reset password state
  const [resetUser, setResetUser] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const fetchUsers = () => {
    api.get('/users').then(r => setUsers(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: '', username: '', full_name: '', password: '', role: 'publisher' });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ email: user.email, username: user.username, full_name: user.full_name, password: '', role: user.role });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, {
          full_name: form.full_name,
          role: form.role,
          is_active: editUser.is_active,
        });
        toast.success('Utilizador atualizado');
      } else {
        if (!form.password) {
          toast.error('Senha é obrigatória para novo utilizador');
          setSaving(false);
          return;
        }
        await api.post('/users', form);
        toast.success('Utilizador criado. Credenciais enviadas por email.');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar utilizador');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success('Utilizador eliminado');
      setDeleteId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao eliminar utilizador');
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Utilizador desativado' : 'Utilizador ativado');
      fetchUsers();
    } catch (err) {
      toast.error('Erro ao atualizar estado');
    }
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    setResetting(true);
    try {
      const res = await api.post(`/users/${resetUser.id}/reset-password`);
      setResetResult(res.data);
      toast.success('Password redefinida com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir password');
      setResetUser(null);
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Password copiada para a área de transferência');
  };

  const closeResetModal = () => {
    setResetUser(null);
    setResetResult(null);
    setShowPassword(false);
  };

  return (
    <div data-testid="users-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('backoffice.users')}</h1>
        <button onClick={openCreate} data-testid="create-user-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-md hover:bg-[#A30000] transition-colors">
          <UserPlus size={14} /> Novo Utilizador
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-gray-400">Nenhum utilizador encontrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Utilizador</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Perfil</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Estado</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-testid={`user-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#CC0000] flex items-center justify-center text-white font-bold text-sm">
                          {u.full_name?.[0] || u.username?.[0] || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{u.full_name}</p>
                          <p className="text-xs text-gray-400">@{u.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-600">{u.email}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role]}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                        {u.is_active ? <Check size={12} /> : <Ban size={12} />}
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                          className="p-1.5 text-gray-400 hover:text-[#CC0000] rounded hover:bg-red-50 transition-colors" title="Editar">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => setResetUser(u)} data-testid={`reset-password-${u.id}`}
                          className="p-1.5 text-gray-400 hover:text-amber-600 rounded hover:bg-amber-50 transition-colors" title="Reset Password">
                          <KeyRound size={14} />
                        </button>
                        <button onClick={() => toggleActive(u)} title={u.is_active ? 'Desativar' : 'Ativar'}
                          className={`p-1.5 rounded transition-colors ${u.is_active ? 'text-gray-400 hover:text-orange-600 hover:bg-orange-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}>
                          {u.is_active ? <Ban size={14} /> : <Check size={14} />}
                        </button>
                        {u.email !== 'claucisco@gmail.com' && (
                          <button onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.id}`}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors" title="Eliminar">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="user-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">{editUser ? 'Editar Utilizador' : 'Novo Utilizador'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm(p => ({ ...p, full_name: e.target.value }))}
                  required
                  data-testid="user-fullname"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              {!editUser && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                      required
                      data-testid="user-email"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input
                      type="text"
                      value={form.username}
                      onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
                      required
                      data-testid="user-username"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="Mínimo 6 caracteres"
                      data-testid="user-password"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}
                  data-testid="user-role"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                >
                  <option value="admin">Administrador</option>
                  <option value="consul">Cônsul</option>
                  <option value="editor">Editor</option>
                  <option value="publisher">Publicador</option>
                  <option value="consular_agent">Agente Consular</option>
                  <option value="financial">Financeiro</option>
                  <option value="auditor">Auditor</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {form.role === 'admin' && 'Acesso total ao sistema'}
                  {form.role === 'consul' && 'Gestão consular e supervisão de serviços'}
                  {form.role === 'editor' && 'Gestão de conteúdos e aprovação de artigos'}
                  {form.role === 'publisher' && 'Criação e publicação de artigos'}
                  {form.role === 'consular_agent' && 'Gestão de agendamentos e cartões consulares'}
                  {form.role === 'financial' && 'Acesso a pagamentos e relatórios financeiros'}
                  {form.role === 'auditor' && 'Visualização de todos os dados (apenas leitura)'}
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} data-testid="save-user"
                  className="flex-1 py-2 bg-[#CC0000] text-white rounded-md text-sm font-medium hover:bg-[#A30000] disabled:opacity-50">
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-user-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="font-bold text-gray-900 mb-2">Confirmar Eliminação</h3>
            <p className="text-sm text-gray-500 mb-5">{t('common.confirm_delete')}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-user"
                className="flex-1 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="reset-password-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            {!resetResult ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <KeyRound className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Reset de Password</h3>
                    <p className="text-sm text-gray-500">Confirme a acção</p>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                  <p className="text-sm text-amber-800">
                    Está prestes a redefinir a password do utilizador <strong>{resetUser.full_name}</strong> ({resetUser.email}).
                  </p>
                  <p className="text-sm text-amber-800 mt-2">
                    Uma nova password temporária será gerada e enviada para o email do utilizador.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button onClick={closeResetModal} className="flex-1 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-password"
                    className="flex-1 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {resetting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        A processar...
                      </>
                    ) : (
                      <>
                        <KeyRound size={16} />
                        Redefinir Password
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-5">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="text-green-600" size={28} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg">Password Redefinida!</h3>
                  <p className="text-sm text-gray-500 mt-1">A nova password foi enviada por email</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-5">
                  <p className="text-xs text-gray-500 mb-2 text-center">Nova Password Temporária</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-3 font-mono text-center text-lg tracking-wider">
                      {showPassword ? resetResult.temp_password : '••••••••••••'}
                    </div>
                    <button 
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title={showPassword ? 'Ocultar' : 'Mostrar'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button 
                      onClick={() => copyToClipboard(resetResult.temp_password)}
                      className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Copiar"
                    >
                      <Copy size={18} />
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5">
                  <p className="text-xs text-blue-800">
                    <strong>Nota:</strong> Se o email não chegar, pode partilhar esta password manualmente com o utilizador de forma segura.
                  </p>
                </div>

                <button onClick={closeResetModal} className="w-full py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-900">
                  Fechar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
